import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { blue } from '@carbon/colors';
import { Layer, Source } from 'react-map-gl';
import type { FillPaint, LinePaint } from 'mapbox-gl';
import { buildPolygonStyle } from 'lib/map';
import { EVAL3_SCOPED_LAYER_IDS } from 'components/map';
import {
  colorRulesAtom,
  inventoryPackageOverridesAtom,
  scopedMapLayersAtom,
  typeCurveOverridesAtom,
  typeCurveScenarioIdAtom,
} from './atoms';
import {
  useCalcLifecycle,
  useColorElements,
  usePolygonHighlightFilter,
} from './hooks';
import { buildColorExpression, packageMapSourceUrl } from './utils';

/**
 * Unit polygon fill + outline colors — a deeper `blue[40]` (#78a9ff) fill than the
 * mock's faint `blue[30]` (so it reads over the basemap, on par with the Tract
 * layer) and a `blue[70]` (#0043ce) outline. Also the color-by-attribute defaults —
 * a unit matching no color rule keeps its flat fill / outline.
 */
const UNIT_FILL_COLOR = blue[40];
const UNIT_LINE_COLOR = blue[70];

/**
 * Unit polygon style — `blue[40]` fill at 0.45 opacity with a 1.5px outline (more
 * pronounced than the mock's 0.25 / 0.75, which read too faint).
 */
const UNIT_STYLE = buildPolygonStyle({
  color: UNIT_FILL_COLOR,
  lineColor: UNIT_LINE_COLOR,
  fillOpacity: 0.45,
  lineOpacity: 1,
  lineWidth: 1.5,
});

interface UnitLayerProps {
  /** The package whose scoped unit tiles are rendered (from `PackageMap`). */
  packageId: number;
  /** The eval-calc id (calc mode) or null — threaded into color/visibility resolution. */
  calculationId: number | null;
}

/**
 * Package-scoped Unit map layer — the `development_areas` MVT tile rendered as a
 * polygon fill + outline (centroid label points excluded). Visibility is driven by
 * the `units` key of `scopedMapLayersAtom` via Mapbox `layout.visibility`, so the
 * source stays mounted and toggling never refetches tiles. The filter is always
 * shape-only; when a row is pinned it additionally restricts to that row's related
 * units (by `name`).
 *
 * The fill AND outline are colored by the units dataset's color rules: the enabled,
 * map-targeting rules resolve to ids (`useColorElements`) that paint matching units
 * (by `name`) their rule color via a Mapbox `match` on both `fill-color` and
 * `line-color`, each falling back to the flat fill / outline color.
 *
 * @param {UnitLayerProps} props - The package id + eval-calc id the tiles are scoped to.
 * @return {JSX.Element} The unit tile source with its fill + outline layers.
 */
export function UnitLayer({ packageId, calculationId }: UnitLayerProps) {
  const { units } = useAtomValue(scopedMapLayersAtom);
  const typeCurveScenarioId = useAtomValue(typeCurveScenarioIdAtom);
  const typeCurveOverrides = useAtomValue(typeCurveOverridesAtom);
  const inventoryPackageOverrides = useAtomValue(inventoryPackageOverridesAtom);
  const layout = { visibility: units ? 'visible' : 'none' } as const;
  const { canFetch: calcTilesReady } = useCalcLifecycle(calculationId);
  // Layer filter = page-filter visibility ∩ pin (memoized in the hook — that
  // stability is load-bearing for smooth zoom; see the hook's comment).
  const filter = usePolygonHighlightFilter(packageId, calculationId);

  // Color-by-attribute (units): resolved ids → a Mapbox `match` over the polygon
  // `name`, falling back to the flat fill. Memoized with the same discipline as the
  // filter (a fresh paint identity re-applies paint + janks zoom); `colorElements`
  // is referentially stable, so this recomputes only on a real color change.
  const colorRules = useAtomValue(colorRulesAtom('units'));
  const mapRules = useMemo(
    () =>
      colorRules.filter((rule) => rule.enabled && rule.applyTo.includes('map')),
    [colorRules]
  );
  const colorElements = useColorElements(packageId, calculationId, mapRules);
  // Color the fill AND the outline by attribute. The match branches are identical
  // (resolved ids → rule color); only the fallback differs — an unmatched unit keeps
  // its flat fill vs its flat outline color.
  const fillColor = useMemo(
    () => buildColorExpression(colorElements, 'name', UNIT_FILL_COLOR),
    [colorElements]
  );
  const lineColor = useMemo(
    () => buildColorExpression(colorElements, 'name', UNIT_LINE_COLOR),
    [colorElements]
  );
  const fillPaint = useMemo<FillPaint>(
    () => ({ ...UNIT_STYLE.fill, 'fill-color': fillColor }),
    [fillColor]
  );
  const linePaint = useMemo<LinePaint>(
    () => ({ ...UNIT_STYLE.line, 'line-color': lineColor }),
    [lineColor]
  );

  return (
    <Source
      id="eval3-units"
      type="vector"
      // The scenario AND both manager folds key this source's filterSet token (INS-10669):
      // its members include the `slot` grain, which holds the Type Curve LTD place ids, so
      // omitting the folds resolves this token against the DEFAULT curves while the
      // slot/LTD layers use the overridden set (PR #7365 review; latent today — the unit
      // tile mints a token only under a `scoped` param nothing emits yet — fixed for
      // symmetry with slot-layer/ltd-slot-layer so it cannot go live wrong).
      url={packageMapSourceUrl(
        packageId,
        'development_areas',
        calcTilesReady ? calculationId : null,
        typeCurveScenarioId,
        typeCurveOverrides,
        inventoryPackageOverrides
      )}
    >
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.unit.fill}
        type="fill"
        source-layer="package-development-areas"
        filter={filter}
        paint={fillPaint}
        layout={layout}
      />
      <Layer
        id={EVAL3_SCOPED_LAYER_IDS.unit.line}
        type="line"
        source-layer="package-development-areas"
        filter={filter}
        paint={linePaint}
        layout={layout}
      />
    </Source>
  );
}
